import React, { useState, useEffect } from 'react';
import { X, ArrowRight, ArrowLeft, Check, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const OnboardingModal = ({ isOpen, onClose, selectedPlan }) => {
  const { getAuthHeaders } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    primaryGoals: [],
    contentSources: [],
    contentStorageTypes: [],
    contentStorageOther: '',
    contentLinks: '',
    platforms: [],
    linkInBio: '',
    linkInBioUrl: '',
    accountStage: '',
    additionalNotes: '',
    selectedTheme: null
  });

  // Load selected theme from localStorage if exists
  useEffect(() => {
    const savedTheme = localStorage.getItem('autopostr_selected_theme');
    if (savedTheme) {
      try {
        const theme = JSON.parse(savedTheme);
        setFormData(prev => ({ ...prev, selectedTheme: theme }));
      } catch (e) {
        console.error('Error parsing selected theme:', e);
      }
    }
  }, [isOpen]);

  const maxPlatforms = (selectedPlan === 'Starter' || selectedPlan === 'AutoPostr Starter') ? 5 : 7;

  const handleOptionSelect = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMultiSelect = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      } else {
        if (field === 'platforms' && current.length >= maxPlatforms) {
          return prev;
        }
        return { ...prev, [field]: [...current, value] };
      }
    });
  };

  // Steps: 1 (goals) -> 2 (content source) -> 2.7 (own content details, if applicable) -> 3 (platforms, starter only) -> 4 (link-in-bio) -> 5 (account stage) -> 6 (notes)
  const handleNext = () => {
    if (currentStep === 1) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      if (formData.contentSources.includes('own')) {
        setCurrentStep(2.7); // Own content details
      } else {
        // Skip to platform selection or link-in-bio
        if (selectedPlan === 'Pro') {
          setFormData(prev => ({ 
            ...prev, 
            platforms: ['tiktok', 'instagram', 'youtube', 'facebook', 'twitter', 'threads', 'linkedin'] 
          }));
          setCurrentStep(4); // Skip to link-in-bio
        } else {
          setCurrentStep(3); // Starter users select platforms
        }
      }
    } else if (currentStep === 2.7) {
      if (selectedPlan === 'Pro') {
        setFormData(prev => ({ 
          ...prev, 
          platforms: ['tiktok', 'instagram', 'youtube', 'facebook', 'twitter', 'threads', 'linkedin'] 
        }));
        setCurrentStep(4); // Skip to link-in-bio
      } else {
        setCurrentStep(3); // Starter users select platforms
      }
    } else if (currentStep === 3) {
      setCurrentStep(4);
    } else if (currentStep === 4) {
      setCurrentStep(5);
    } else if (currentStep === 5) {
      setCurrentStep(6);
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else if (currentStep === 2.7) {
      setCurrentStep(2);
    } else if (currentStep === 3) {
      if (formData.contentSources.includes('own')) {
        setCurrentStep(2.7);
      } else {
        setCurrentStep(2);
      }
    } else if (currentStep === 4) {
      if (selectedPlan === 'Pro') {
        if (formData.contentSources.includes('own')) {
          setCurrentStep(2.7);
        } else {
          setCurrentStep(2);
        }
      } else {
        setCurrentStep(3);
      }
    } else if (currentStep === 5) {
      setCurrentStep(4);
    } else if (currentStep === 6) {
      setCurrentStep(5);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    const submissionData = {
      ...formData,
      plan: selectedPlan,
      timestamp: new Date().toISOString()
    };

    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const STRIPE_API_URL = process.env.REACT_APP_BACKEND_URL;

    // Save submission to main API (best-effort - never blocks checkout)
    try {
      await fetch(`${API_URL}/api/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(submissionData)
      });
    } catch (error) {
      console.error('Error saving submission:', error);
    }

    localStorage.setItem('autopostr_onboarding', JSON.stringify(submissionData));
    localStorage.removeItem('autopostr_selected_theme');

    // Create Stripe Checkout via Emergent Stripe-bridge (supports promo/affiliate codes)
    const planMap = {
      'Starter': 'starter',
      'AutoPostr Starter': 'starter',
      'Pro': 'pro',
      'Premium': 'premium',
      'AutoPostr Premium': 'premium'
    };
    const planKey = planMap[selectedPlan] || 'starter';

    try {
      const res = await fetch(`${STRIPE_API_URL}/api/stripe/create-pro-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          plan: planKey,
          billing_period: 'monthly',
          email: submissionData.email || undefined,
          onboarding: {
            goals: formData.primaryGoals,
            platforms: formData.platforms,
            stage: formData.accountStage
          }
        })
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      alert(data.detail || 'Could not start checkout. Please try again.');
    } catch (e) {
      console.error('Checkout error:', e);
      alert('Error connecting to payment system. Please try again.');
    }
    setIsSubmitting(false);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return formData.primaryGoals.length > 0;
      case 2: return formData.contentSources.length > 0;
      case 2.7: return (formData.contentStorageTypes || []).length > 0;
      case 3: return formData.platforms.length > 0;
      case 4: return formData.linkInBio !== '';
      case 5: return formData.accountStage !== '';
      case 6: return true;
      default: return true;
    }
  };

  if (!isOpen) return null;

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white mb-2">What is your primary goal with this package?</h2>
            <p className="text-white/60 mb-6">Select all that apply - This helps us customize your automation strategy.</p>
            
            {[
              { value: 'faceless', label: 'Grow a faceless page' },
              { value: 'affiliate', label: 'Promote affiliate offers' },
              { value: 'digital', label: 'Sell digital products (PLR / MRR)' },
              { value: 'theme', label: 'Build a theme page for monetization' },
              { value: 'audience', label: 'Grow audience first, monetize later' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => handleMultiSelect('primaryGoals', option.value)}
                className={`w-full p-4 rounded-xl border text-left transition-all duration-200 ${
                  formData.primaryGoals.includes(option.value)
                    ? 'border-emerald-500 bg-emerald-500/10 text-white'
                    : 'border-white/10 bg-white/5 text-white/80 hover:border-white/20'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    formData.primaryGoals.includes(option.value) ? 'border-emerald-500 bg-emerald-500' : 'border-white/30'
                  }`}>
                    {formData.primaryGoals.includes(option.value) && <Check size={12} className="text-white" />}
                  </div>
                  {option.label}
                </div>
              </button>
            ))}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white mb-2">Where is your content currently stored?</h2>
            <p className="text-white/60 mb-6">Select all that apply - This allows us to set up your automation correctly.</p>
            
            {[
              { value: 'autopostr', label: "I want to use AutoPostr's content library" },
              { value: 'own', label: 'I will use my own content' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => handleMultiSelect('contentSources', option.value)}
                className={`w-full p-4 rounded-xl border text-left transition-all duration-200 ${
                  formData.contentSources.includes(option.value)
                    ? 'border-emerald-500 bg-emerald-500/10 text-white'
                    : 'border-white/10 bg-white/5 text-white/80 hover:border-white/20'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    formData.contentSources.includes(option.value) ? 'border-emerald-500 bg-emerald-500' : 'border-white/30'
                  }`}>
                    {formData.contentSources.includes(option.value) && <Check size={12} className="text-white" />}
                  </div>
                  {option.label}
                </div>
              </button>
            ))}
          </div>
        );

      case 2.7:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Where is your content currently stored?</h2>
              <p className="text-white/60 mb-6">Select all that apply - We'll connect to your content sources.</p>
              
              <div className="grid grid-cols-2 gap-3 mb-6">
                {[
                  { value: 'gdrive', label: 'Google Drive' },
                  { value: 'youtube', label: 'YouTube channel' },
                  { value: 'dropbox', label: 'Dropbox' },
                  { value: 'clone_tiktok', label: 'Clone a TikTok Page' },
                  { value: 'clone_instagram', label: 'Clone an Instagram Page' },
                  { value: 'vizard', label: 'Vizard.ai Projects' },
                  { value: 'other', label: 'Other' }
                ].map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleMultiSelect('contentStorageTypes', option.value)}
                    className={`p-3 rounded-xl border text-center transition-all duration-200 ${
                      (formData.contentStorageTypes || []).includes(option.value)
                        ? 'border-emerald-500 bg-emerald-500/10 text-white'
                        : 'border-white/10 bg-white/5 text-white/80 hover:border-white/20'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                        (formData.contentStorageTypes || []).includes(option.value) ? 'border-emerald-500 bg-emerald-500' : 'border-white/30'
                      }`}>
                        {(formData.contentStorageTypes || []).includes(option.value) && <Check size={10} className="text-white" />}
                      </div>
                      {option.label}
                    </div>
                  </button>
                ))}
              </div>

              {(formData.contentStorageTypes || []).includes('other') && (
                <input
                  type="text"
                  placeholder="Please specify..."
                  value={formData.contentStorageOther}
                  onChange={(e) => setFormData(prev => ({ ...prev, contentStorageOther: e.target.value }))}
                  className="w-full p-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-emerald-500 focus:outline-none mb-4"
                />
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white mb-2">Which platforms would you like us to post on?</h2>
            <p className="text-white/60 mb-2">
              {selectedPlan === 'Starter' 
                ? `Select up to ${maxPlatforms} platforms (Starter plan)` 
                : 'Select all that apply'}
            </p>
            {selectedPlan === 'Starter' && (
              <p className="text-emerald-400 text-sm mb-4">
                {formData.platforms.length}/{maxPlatforms} platforms selected
              </p>
            )}
            
            {[
              { value: 'tiktok', label: 'TikTok' },
              { value: 'instagram', label: 'Instagram Reels' },
              { value: 'youtube', label: 'YouTube Shorts' },
              { value: 'facebook', label: 'Facebook Reels' },
              { value: 'twitter', label: 'X (Twitter)' }
            ].map(platform => {
              const isSelected = formData.platforms.includes(platform.value);
              const isDisabled = !isSelected && formData.platforms.length >= maxPlatforms;
              
              return (
                <button
                  key={platform.value}
                  onClick={() => !isDisabled && handleMultiSelect('platforms', platform.value)}
                  disabled={isDisabled}
                  className={`w-full p-4 rounded-xl border text-left transition-all duration-200 ${
                    isSelected
                      ? 'border-emerald-500 bg-emerald-500/10 text-white'
                      : isDisabled
                        ? 'border-white/5 bg-white/5 text-white/30 cursor-not-allowed'
                        : 'border-white/10 bg-white/5 text-white/80 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      isSelected ? 'border-emerald-500 bg-emerald-500' : 'border-white/30'
                    }`}>
                      {isSelected && <Check size={12} className="text-white" />}
                    </div>
                    {platform.label}
                  </div>
                </button>
              );
            })}
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white mb-2">Do you already have a link-in-bio webpage?</h2>
            <p className="text-white/60 mb-6">We can create a simple monetization-ready link-in-bio if needed.</p>
            
            {[
              { value: 'yes', label: 'Yes (please provide the link)' },
              { value: 'no', label: 'No — I would like AutoPostr to create one for me' },
              { value: 'unsure', label: "I'm not sure" }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => handleOptionSelect('linkInBio', option.value)}
                className={`w-full p-4 rounded-xl border text-left transition-all duration-200 ${
                  formData.linkInBio === option.value
                    ? 'border-emerald-500 bg-emerald-500/10 text-white'
                    : 'border-white/10 bg-white/5 text-white/80 hover:border-white/20'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    formData.linkInBio === option.value ? 'border-emerald-500 bg-emerald-500' : 'border-white/30'
                  }`}>
                    {formData.linkInBio === option.value && <Check size={12} className="text-white" />}
                  </div>
                  {option.label}
                </div>
              </button>
            ))}

            {formData.linkInBio === 'yes' && (
              <input
                type="url"
                placeholder="Enter your link-in-bio URL..."
                value={formData.linkInBioUrl}
                onChange={(e) => setFormData(prev => ({ ...prev, linkInBioUrl: e.target.value }))}
                className="w-full p-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-emerald-500 focus:outline-none mt-4"
              />
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white mb-2">What stage is your account currently at?</h2>
            <p className="text-white/60 mb-6">This helps us apply the safest posting strategy.</p>
            
            {[
              { value: 'new', label: 'Brand new account' },
              { value: 'small', label: 'Small account (under 10k followers)' },
              { value: 'established', label: 'Established account (10k+ followers)' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => handleOptionSelect('accountStage', option.value)}
                className={`w-full p-4 rounded-xl border text-left transition-all duration-200 ${
                  formData.accountStage === option.value
                    ? 'border-emerald-500 bg-emerald-500/10 text-white'
                    : 'border-white/10 bg-white/5 text-white/80 hover:border-white/20'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    formData.accountStage === option.value ? 'border-emerald-500 bg-emerald-500' : 'border-white/30'
                  }`}>
                    {formData.accountStage === option.value && <Check size={12} className="text-white" />}
                  </div>
                  {option.label}
                </div>
              </button>
            ))}
          </div>
        );

      case 6:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white mb-2">Anything else we should know to serve you better?</h2>
            <p className="text-white/60 mb-6">Optional - Share any additional details or preferences.</p>
            
            <textarea
              placeholder="Type your message here..."
              value={formData.additionalNotes}
              onChange={(e) => setFormData(prev => ({ ...prev, additionalNotes: e.target.value }))}
              rows={5}
              className="w-full p-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-emerald-500 focus:outline-none resize-none"
            />

            {formData.selectedTheme && (
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                <p className="text-emerald-400 text-sm font-medium">Selected Theme Page:</p>
                <p className="text-white">{formData.selectedTheme.title} - £{formData.selectedTheme.price}</p>
              </div>
            )}

            <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
              <p className="text-white/60 text-sm">
                By clicking "Complete Setup & Start Posting", you confirm you have the rights to use all content provided and agree to AutoPostr's automation terms.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getTotalSteps = () => 6;
  const getDisplayStep = () => {
    if (currentStep === 2.7) return 2;
    if (currentStep > 2.7) return currentStep - 0.7;
    return currentStep;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-[#0f0f0f] border border-white/10 rounded-3xl p-6 md:p-8">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors"
        >
          <X size={20} className="text-white/60" />
        </button>

        <div className="flex items-center gap-1 mb-8">
          {Array.from({ length: getTotalSteps() }).map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                i < getDisplayStep() ? 'bg-emerald-500' : 'bg-white/10'
              }`}
            />
          ))}
        </div>

        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-6">
          <span className="text-xs font-medium text-emerald-400">{selectedPlan} Plan</span>
        </div>

        {renderStep()}

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
          {currentStep > 1 ? (
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-white/60 hover:text-white transition-colors"
            >
              <ArrowLeft size={18} />
              Back
            </button>
          ) : (
            <div />
          )}

          {currentStep < 6 ? (
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all duration-200 ${
                canProceed()
                  ? 'bg-white text-black hover:bg-white/90'
                  : 'bg-white/20 text-white/40 cursor-not-allowed'
              }`}
            >
              Continue
              <ArrowRight size={18} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-all duration-200"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Complete Setup & Start Posting
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
